/**
 * The Shop Language: the single list of languages an embedding shop may hand
 * over as `?lang=`, shared by the web bundle, the API (Accept-Language and
 * DTO enums) and the MCP tools. A language enters this list only when its UI
 * locale file and its catalogue translation file are both complete — until
 * then it parses as unsupported and falls back to German.
 */

export const SUPPORTED_LANGUAGES = [
  'de',
  'en',
  'fr',
  'es',
  'it',
  'nl',
  'pt',
  'pl',
] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** German is the fallback: the first embedding shops are German-speaking. */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'de';

export function isSupportedLanguage(
  value: string | null | undefined
): value is SupportedLanguage {
  return !!value && SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);
}

/**
 * A parsed language tag: the base language that selects strings and
 * translations, plus the normalised BCP 47 locale (`pt-BR`, `en-GB`) that
 * `Intl` formatting should keep, because a Brazilian customer and a
 * Portuguese one share strings but not number layouts.
 */
export interface ParsedLanguage {
  language: SupportedLanguage;
  locale: string;
}

const TAG_PATTERN = /^([a-z]{2,3})(?:[-_]([a-z0-9]{2,4}))?/i;

/**
 * Parses a language tag from a URL parameter or a stored value. Accepts a
 * region suffix separated by `-` or `_`, ignores casing and whitespace and
 * returns null for anything unsupported or empty.
 */
export function parseLanguageTag(
  raw: string | null | undefined
): ParsedLanguage | null {
  if (!raw) return null;
  const match = TAG_PATTERN.exec(raw.trim());
  if (!match) return null;
  const base = match[1].toLowerCase();
  if (!isSupportedLanguage(base)) return null;
  const region = match[2]?.toUpperCase();
  return { language: base, locale: region ? `${base}-${region}` : base };
}

/** The base language of a tag, or null when unsupported. */
export function parseLanguage(
  raw: string | null | undefined
): SupportedLanguage | null {
  return parseLanguageTag(raw)?.language ?? null;
}

/**
 * Parses an `Accept-Language` header. Only the first entry counts: the web
 * client sends exactly one tag, and for any other caller the first entry is
 * the preferred one. Quality weights are ignored.
 */
export function parseAcceptLanguage(
  header: string | null | undefined
): ParsedLanguage | null {
  if (!header) return null;
  const first = header.split(',')[0]?.split(';')[0];
  return parseLanguageTag(first);
}
